import { CliError, EXIT_INPUT } from "../../common.js";
import { requireScriptV3 } from "./v3/parse.js";
import type { ScriptV3 } from "./v3/types.js";
import { requireScriptV4, requireStructurallyReadableScriptV4 } from "./v4/parse.js";
import type { ScriptV4 } from "./v4/types.js";
import { validateScriptV4 } from "./v4/validation.js";
import { validateScriptV3 } from "./v3/validation.js";
import type { ValidationResult } from "./shared/validation.js";

export type ScriptVersion = 3 | 4;
export type ScriptDocument = ScriptV3 | ScriptV4;
export const SUPPORTED_SCRIPT_VERSIONS: readonly ScriptVersion[] = [3, 4];

type Versioned<T> = Readonly<Record<ScriptVersion, T>>;

export interface ScriptDialect {
  readonly version: ScriptVersion;
  readonly require: (value: unknown) => ScriptDocument;
  readonly requireStructurallyReadable: (value: unknown) => ScriptDocument;
  readonly validate: (value: unknown) => ValidationResult;
}

const DIALECTS: Versioned<ScriptDialect> = {
  3: {
    version: 3,
    require: requireScriptV3,
    requireStructurallyReadable: requireScriptV3,
    validate: validateScriptV3,
  },
  4: {
    version: 4,
    require: requireScriptV4,
    requireStructurallyReadable: requireStructurallyReadableScriptV4,
    validate: validateScriptV4,
  },
};

export function isSupportedScriptVersion(value: unknown): value is ScriptVersion {
  return typeof value === "number"
    && SUPPORTED_SCRIPT_VERSIONS.some((version) => version === value);
}

export function scriptVersionOf(value: unknown): ScriptVersion {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new CliError("script.json must be an object with an explicit version.", {
            exitCode: EXIT_INPUT,
            errorCode: "SCRIPT_VERSION_MISSING",
            data: { expected: ["version: 3 or version: 4"], actual: [Array.isArray(value) ? "array" : typeof value] },
          });
  }
  const version = (value as Record<string, unknown>).version;
  if (isSupportedScriptVersion(version)) return version;
  throw new CliError("script.json must declare version 3 or version 4.", {
          exitCode: EXIT_INPUT,
          errorCode: version === undefined ? "SCRIPT_VERSION_MISSING" : "SCRIPT_VERSION_UNSUPPORTED",
          hint: "Use an explicit supported script version; scriptctl does not infer a version from document shape.",
          data: { expected: ["version: 3 or version: 4"], actual: [String(version)] },
        });
}

export function dialectFor(value: unknown): ScriptDialect {
  return DIALECTS[scriptVersionOf(value)];
}

export function requireScriptDocument(value: unknown): ScriptDocument {
  return dialectFor(value).require(value);
}

export function requireStructurallyReadableScriptDocument(value: unknown): ScriptDocument {
  return dialectFor(value).requireStructurallyReadable(value);
}

export function validateScriptDocument(value: unknown): ValidationResult {
  return dialectFor(value).validate(value);
}
